// Reactive core — fine-grained signals. No virtual DOM, no reconciliation.
// Isomorphic: runs unchanged in the Worker (SSR) and the browser (hydration).
//
// A signal holds a value and a set of subscribers. Reads inside an `effect`
// (or `computed`) auto-register a dependency; writes notify exactly the
// effects that read them. `batch` coalesces writes into a single flush.

export interface Signal<T> {
  value: T;
  /** Manual subscription. Called on every change (not immediately). Returns a disposer. */
  subscribe(fn: (value: T) => void): () => void;
  /** @internal Brand for runtime detection across module boundaries. */
  readonly [IS_SIGNAL]: true;
}

export interface ReadonlySignal<T> extends Signal<T> {
  readonly value: T;
}

export const IS_SIGNAL = Symbol.for("hc.signal");

interface EffectCtx {
  run(): void;
  deps: Set<Set<EffectCtx>>;
  disposed: boolean;
}

let activeEffect: EffectCtx | null = null;
let batchDepth = 0;
const pending = new Set<EffectCtx>();

function track(subs: Set<EffectCtx>): void {
  if (activeEffect) {
    subs.add(activeEffect);
    activeEffect.deps.add(subs);
  }
}

function schedule(effect: EffectCtx): void {
  if (effect.disposed) return;
  pending.add(effect);
  if (batchDepth === 0) flush();
}

function flush(): void {
  // Run scheduled effects. Guard against an effect scheduling itself into an
  // endless loop within a single flush by snapshotting per iteration.
  let guard = 0;
  while (pending.size > 0) {
    if (++guard > 100_000) {
      pending.clear();
      throw new Error("hc.signal: effect flush did not settle (cycle?)");
    }
    const batch = [...pending];
    pending.clear();
    for (const effect of batch) {
      if (!effect.disposed) effect.run();
    }
  }
}

export function signal<T>(initial: T): Signal<T> {
  let val = initial;
  const subs = new Set<EffectCtx>();
  const listeners = new Set<(value: T) => void>();

  return {
    [IS_SIGNAL]: true,
    get value(): T {
      track(subs);
      return val;
    },
    set value(next: T) {
      if (Object.is(val, next)) return;
      val = next;
      // Snapshot subscribers; running an effect re-tracks its deps.
      for (const effect of [...subs]) schedule(effect);
      if (listeners.size > 0) for (const fn of [...listeners]) fn(next);
    },
    subscribe(fn: (value: T) => void): () => void {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

export function effect(fn: () => void): () => void {
  const ctx: EffectCtx = {
    deps: new Set(),
    disposed: false,
    run(): void {
      if (ctx.disposed) return;
      cleanup(ctx);
      const prev = activeEffect;
      activeEffect = ctx;
      try {
        fn();
      } finally {
        activeEffect = prev;
      }
    },
  };
  ctx.run();
  return () => {
    if (ctx.disposed) return;
    ctx.disposed = true;
    cleanup(ctx);
  };
}

function cleanup(ctx: EffectCtx): void {
  for (const dep of ctx.deps) dep.delete(ctx);
  ctx.deps.clear();
}

export function computed<T>(fn: () => T): ReadonlySignal<T> {
  const inner = signal<T>(undefined as T);
  effect(() => {
    inner.value = fn();
  });
  return {
    [IS_SIGNAL]: true,
    get value(): T {
      return inner.value;
    },
    subscribe: inner.subscribe,
  };
}

export function batch(fn: () => void): void {
  batchDepth++;
  try {
    fn();
  } finally {
    batchDepth--;
    if (batchDepth === 0) flush();
  }
}

export function isSignal(x: unknown): x is Signal<unknown> {
  return typeof x === "object" && x !== null && (x as Record<symbol, unknown>)[IS_SIGNAL] === true;
}

/** Read a value that may be a plain value, a signal, or a zero-arg function. */
export function read<T>(x: T | Signal<T> | (() => T)): T {
  if (isSignal(x)) return (x as Signal<T>).value;
  if (typeof x === "function") return (x as () => T)();
  return x as T;
}
